import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import ReactGA from "react-ga4";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function Learning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: "/learning",
      title: "Learning Center",
    });
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const panelBg = "rgba(255,255,255,0.04)";
  const darkPanel = "rgba(0,0,0,0.18)";

  const collectionSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Parish Audio Learning Center",
      description:
        "Educational articles about audio effects, mixing, sound design, and music production from Parish Audio.",
      url: "https://parishaudio.com/learning",
      hasPart: [
        {
          "@type": "Article",
          name: "Chorus",
          url: "https://parishaudio.com/learning/chorus",
        },
        {
          "@type": "Article",
          name: "Compression",
          url: "https://parishaudio.com/learning/compression",
        },
        {
          "@type": "Article",
          name: "Delay",
          url: "https://parishaudio.com/learning/delay",
        },
        {
          "@type": "Article",
          name: "Distortion",
          url: "https://parishaudio.com/learning/distortion",
        },
        {
          "@type": "Article",
          name: "Equalization",
          url: "https://parishaudio.com/learning/equalization",
        },
        {
          "@type": "Article",
          name: "Overdrive",
          url: "https://parishaudio.com/learning/overdrive",
        },
        {
          "@type": "Article",
          name: "Reverb",
          url: "https://parishaudio.com/learning/reverb",
        },
        {
          "@type": "Article",
          name: "Overdrive vs Distortion vs Fuzz: What’s the Difference?",
          url: "https://parishaudio.com/learning/compare-distortion-overdrive-fuzz",
        },
      ],
    }),
    []
  );

  const categories = [
    {
      title: "Chorus",
      description:
        "Learn how chorus adds width, movement, and a lush ensemble-like texture to a sound.",
      to: "/learning/chorus",
    },
    {
      title: "Compression",
      description:
        "Understand how compression shapes dynamics, sustain, punch, and perceived loudness.",
      to: "/learning/compression",
    },
    {
      title: "Delay",
      description:
        "Explore echoes, rhythmic repeats, slapback, feedback, and stereo delay techniques.",
      to: "/learning/delay",
    },
    {
      title: "Distortion",
      description:
        "See how distortion changes tone, harmonics, aggression, and character in a mix.",
      to: "/learning/distortion",
    },
    {
      title: "Equalization (EQ)",
      description:
        "Learn how EQ shapes tone, clarity, balance, and space across the frequency spectrum.",
      to: "/learning/equalization",
    },
    {
      title: "Overdrive",
      description:
        "Discover the smoother side of saturation and how overdrive differs from harder clipping.",
      to: "/learning/overdrive",
    },
    {
      title: "Reverb",
      description:
        "Study ambience, space, decay, depth, and the role of reverb in production and sound design.",
      to: "/learning/reverb",
    },
  ];

  const featuredTopics = [
    {
      title: "Overdrive vs Distortion vs Fuzz: What’s the Difference?",
      description:
        "Compare three classic gain styles and learn how each one affects feel, harmonics, and tone.",
      to: "/learning/compare-distortion-overdrive-fuzz",
    },
  ];

  const highlights = [
    {
      title: "Audio effect basics",
      text: "Clear explanations of core effects used in mixing, production, and sound design.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Practical concepts",
      text: "Learn what controls do, when to use them, and how they affect real-world sound.",
      icon: <LibraryMusicRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Easy navigation",
      text: "Browse by effect type or jump straight into focused comparison topics.",
      icon: <MenuBookRoundedIcon sx={{ color: accent }} />,
    },
  ];

  return (
    <>
      <Seo
        title="Learning Center | Parish Audio"
        description="Explore educational articles from Parish Audio covering chorus, compression, delay, distortion, EQ, overdrive, reverb, and related music production topics."
        canonical="https://parishaudio.com/learning"
        image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
        imageAlt="Recording studio background for the Parish Audio Learning Center"
        schema={collectionSchema}
      />

      <Box
        sx={{
          minHeight: height - 130,
          backgroundImage: `linear-gradient(rgba(0,0,0,0.72), rgba(0,0,0,0.72)), url("/RecordingStudioStockImage1.jpg")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          py: 3,
        }}
      >
        <Box sx={{ width: "95%", maxWidth: 1100, mx: "auto" }}>
          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              background: `linear-gradient(180deg, ${style.color1} 0%, rgba(22,32,48,0.98) 100%)`,
              borderRadius: 3,
              color: style.color4,
              boxShadow: 12,
            }}
          >
            <Stack spacing={3}>
              <Box ref={topContainer}>
                <Chip
                  label="Learning Center"
                  sx={{
                    mb: 1.5,
                    color: style.color4,
                    backgroundColor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                />

                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2rem", md: "3rem" },
                    fontWeight: 800,
                    lineHeight: 1.05,
                    mb: 1.25,
                    color: style.color4,
                  }}
                >
                  Learn Audio Effects and Production Concepts
                </Typography>

                <Typography
                  sx={{
                    color: style.color3,
                    fontSize: { xs: "1rem", md: "1.1rem" },
                    lineHeight: 1.75,
                    maxWidth: 900,
                  }}
                >
                  The Parish Audio Learning Center is a growing collection of
                  educational articles about audio effects, tone shaping, sound
                  design, and music production. Browse by category below or jump
                  into featured comparison topics.
                </Typography>
              </Box>

              <Grid container spacing={2} alignItems="stretch">
                {highlights.map((item) => (
                  <Grid item xs={12} md={4} key={item.title} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        backgroundColor: panelBg,
                        boxShadow: 6,
                        width: "100%",
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="flex-start">
                        {item.icon}
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: style.color4,
                              mb: 0.4,
                            }}
                          >
                            {item.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: style.color3, lineHeight: 1.6 }}
                          >
                            {item.text}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Grid container spacing={2.25} alignItems="stretch">
                <Grid item xs={12} md={8} sx={{ display: "flex" }}>
                  <Paper
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      borderRadius: 3,
                      backgroundColor: panelBg,
                      boxShadow: 8,
                      width: "100%",
                    }}
                  >
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                        <Typography
                          variant="h2"
                          sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                        >
                          Categories
                        </Typography>
                      </Stack>

                      <Grid container spacing={2} alignItems="stretch">
                        {categories.map((item) => (
                          <Grid item xs={12} sm={6} key={item.to} sx={{ display: "flex" }}>
                            <Paper
                              sx={{
                                p: 2,
                                borderRadius: 3,
                                backgroundColor: darkPanel,
                                boxShadow: 4,
                                width: "100%",
                                display: "flex",
                              }}
                            >
                              <Stack spacing={1.1} sx={{ width: "100%", height: "100%" }}>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    color: style.color4,
                                    fontSize: "1.05rem",
                                  }}
                                >
                                  {item.title}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: style.color3,
                                    lineHeight: 1.65,
                                    flexGrow: 1,
                                  }}
                                >
                                  {item.description}
                                </Typography>

                                <Button
                                  component={RouterLink}
                                  to={item.to}
                                  variant="outlined"
                                  sx={{
                                    alignSelf: "flex-start",
                                    mt: "auto",
                                    borderColor: accent,
                                    color: style.color4,
                                    fontWeight: 700,
                                    "&:hover": {
                                      borderColor: style.color4,
                                      backgroundColor: "rgba(255,255,255,0.04)",
                                    },
                                  }}
                                >
                                  Read Article
                                </Button>
                              </Stack>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                  <Paper
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      borderRadius: 3,
                      backgroundColor: panelBg,
                      boxShadow: 8,
                      width: "100%",
                    }}
                  >
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CompareArrowsRoundedIcon sx={{ color: accent }} />
                        <Typography
                          variant="h2"
                          sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                        >
                          Featured Topic
                        </Typography>
                      </Stack>

                      {featuredTopics.map((item) => (
                        <Paper
                          key={item.to}
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            backgroundColor: darkPanel,
                            boxShadow: 4,
                          }}
                        >
                          <Stack spacing={1.1}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: style.color4,
                                fontSize: "1.05rem",
                                lineHeight: 1.4,
                              }}
                            >
                              {item.title}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{ color: style.color3, lineHeight: 1.65 }}
                            >
                              {item.description}
                            </Typography>

                            <Button
                              component={RouterLink}
                              to={item.to}
                              variant="outlined"
                              sx={{
                                alignSelf: "flex-start",
                                borderColor: accent,
                                color: style.color4,
                                fontWeight: 700,
                                "&:hover": {
                                  borderColor: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.04)",
                                },
                              }}
                            >
                              Read Topic
                            </Button>
                          </Stack>
                        </Paper>
                      ))}

                      <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                      <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                        Looking for more? New learning content can be added over time
                        as the library grows.
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              <Paper
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.03)",
                  boxShadow: 4,
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                >
                  <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                    You can also browse Parish Audio plugins or return to the home page.
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      component={RouterLink}
                      to="/plugins"
                      variant="outlined"
                      sx={{
                        borderColor: accent,
                        color: style.color4,
                        fontWeight: 700,
                        "&:hover": {
                          borderColor: style.color4,
                          backgroundColor: "rgba(255,255,255,0.04)",
                        },
                      }}
                    >
                      Plugins
                    </Button>

                    <Button
                      component={RouterLink}
                      to="/"
                      variant="outlined"
                      sx={{
                        borderColor: accent,
                        color: style.color4,
                        fontWeight: 700,
                        "&:hover": {
                          borderColor: style.color4,
                          backgroundColor: "rgba(255,255,255,0.04)",
                        },
                      }}
                    >
                      Home
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </>
  );
}


// import {React, useState, useContext, useEffect, useRef} from "react"
// import {useStyle} from '../../context/styleContext/styleContext'
// import ReactGA from 'react-ga4';
// import { Paper } from '@mui/material';
// import { Container, Typography, Divider, Box, List, ListItem, ListItemText } from "@mui/material";


// export default function Learning(){

//   const { style, setStyle } = useStyle();
//   const [width, setWidth] = useState(window.innerWidth)

// function handleWindowSizeChange() {
//     setWidth(window.innerWidth);
// }
// useEffect(() => {
//     window.addEventListener('resize', handleWindowSizeChange);
//     return () => {
//         window.removeEventListener('resize', handleWindowSizeChange);
//     }
// }, []);

// const isMobile = width <= 768;

// useEffect(() => {
//   document.title = 'Parish Audio | Cutting-Edge Audio Processing | Learning Center';
// }, []);

// ReactGA.send({
//     hitType:"pageview",
//     page:"learning/delay",
//     title:"learning/delay",
//   })

//   const [height, setHeight] = useState(window.innerHeight)

//   useEffect(() => {
//     function handleResize() {      
//       setWidth(window.innerWidth)
//       setHeight(window.innerHeight)
//   }
  
//     window.addEventListener('resize', handleResize)
//   })

// const topContainer = useRef();
//     useEffect(() => {
//         // To make sure page starts from the top
//         topContainer.current.scrollIntoView({ block: "end" });
//     }, []);




//     return(
//         <>
//         <div style={{display:'flex', backgroundImage: `url("/RecordingStudioStockImage1.jpg")`, backgroundSize: "cover", backgroundPosition: "center", marginBottom:'0px', flex:1, minHeight:height-130, color:style.color4, padding:'0px'}}>
//         <div style={{display:'flex', flex:1}}>

//         <div className="dark-alpha-80" style={{flex:1}}>
// <div style={{width:'95%', maxWidth:'1000px', margin:'auto'}}>



//         <h1 ref={topContainer} style={{marginTop:'20px'}}>Learning Center</h1>

//         <Divider className="mobile-card-divider" />
//         <div style={{minHeight:height - 400}}>
//         <h2 style={{marginBottom:'20px'}}>Categories</h2>
//         <ul style={{}}>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/chorus'>Chorus</a>
//             </li>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/compression'>Compression</a>
//             </li>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/delay'>Delay</a>
//             </li>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/distortion'>Distortion</a>
//             </li>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/equalization'>Equalization (EQ)</a>
//             </li>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/overdrive'>Overdrive</a>
//             </li>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/reverb'>Reverb</a>
//             </li>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/reverb'>Reverb</a>
//             </li>
//         </ul>
//         </div>

// <h2>Topics:</h2>


// <ul style={{}}>
//             <li style={{marginBottom:'10px'}}>
//                 <a href='/learning/compare-distortion-overdrive-fuzz'>Overdrive vs Distortion vs Fuzz: What’s the Difference?</a>
//             </li>
//             </ul>

//         <Divider className="mobile-card-divider" />
//         <div style={{textAlign:"center"}}>
//         <a href="/home" style={{margin:'10px'}} >Home </a> 
//         <a href="/" style={{margin:'10px'}} >Plugins</a>
//         </div>

//   </div>
//   <div>

  
//       </div>
     

//       </div>

//       </div>
// </div>




        
//         </>

//     )
// }